import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const API = API_BASE_URL;

/**
 * PlacedTab — edge-to-edge table of placed/hired candidates.
 */
const PlacedTab = ({ pipelineId, onCardClick, onReload }) => {
  const [placed, setPlaced] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const token = localStorage.getItem('authToken');
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadPlaced(); }, [pipelineId]);

  const loadPlaced = async () => {
    try {
      const res = await axios.get(`${API}/api/pipelines/${pipelineId}/cards?status=placed`, { headers: authHeaders });
      setPlaced(res.data || []);
    } catch (err) { console.error('Error loading placed:', err); }
    finally { setLoading(false); }
  };

  const filtered = placed.filter(c =>
    (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.current_title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.current_company || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const timeToPlace = (created, updated) => {
    if (!created || !updated) return '—';
    const days = Math.floor((new Date(updated) - new Date(created)) / (1000 * 60 * 60 * 24));
    if (days < 7) return `${days}d`;
    if (days < 30) return `${Math.floor(days / 7)}w`;
    return `${Math.floor(days / 30)}mo`;
  };

  if (loading) return <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div></div>;

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="px-4 py-2 flex items-center gap-3 border-b bg-white flex-shrink-0">
        <div className="relative flex-1 max-w-xs">
          <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search placed..." className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500" />
          <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <span className="text-[11px] text-gray-400 tabular-nums"><span className="font-medium text-green-600">{placed.length}</span> placement{placed.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <svg className="w-10 h-10 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-gray-400">{searchTerm ? 'No matches' : 'No placements yet'}</p>
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Candidate</th>
                <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Placed Date</th>
                <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Time to Place</th>
                <th className="px-4 py-2 text-center text-[10px] font-medium text-gray-500 uppercase tracking-wider">Client Rating</th>
                <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(card => (
                <tr key={card.id} onClick={() => onCardClick?.(card)} className="hover:bg-green-50/50 cursor-pointer transition-colors">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-green-100 text-green-600 text-[9px] font-bold flex items-center justify-center flex-shrink-0">✓</div>
                      <div>
                        <p className="text-xs font-medium text-gray-900">{card.name}</p>
                        <p className="text-[10px] text-gray-500">{card.current_title}{card.current_company ? ` · ${card.current_company}` : ''}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmtDate(card.updated_at)}</td>
                  <td className="px-4 py-2.5">
                    <span className="text-xs font-medium text-green-700">{timeToPlace(card.created_at, card.updated_at)}</span>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {card.client_rating ? (
                      <span className="text-xs text-yellow-500">{'★'.repeat(card.client_rating)}{'☆'.repeat(5 - card.client_rating)}</span>
                    ) : <span className="text-[10px] text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500 truncate max-w-[200px]">{card.placement_notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PlacedTab;
